import { useQuery } from "@tanstack/react-query";

import { getAllCarriers } from "@/api/carriers/carriers";
import { ICarrier } from "@/api/carriers/carriers.types";
import { SearchField } from "@/components/admin";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { formatDate } from "@/utils";
import { GridColDef, GridRowParams, GridSortModel } from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useRef, useState } from "react";
import { DeleteCarrierButton } from "./DeleteCarrierButton";
import { EditCarrierButton } from "./EditCarrierButton";

const columns: GridColDef<ICarrier>[] = [
    {
        field: "carrier_name",
        headerName: "Carrier Name",
        display: "flex",
    },
    {
        field: "created_at",
        headerName: "Date Created",
        type: "date",
        display: "flex",
        valueFormatter: (value) => {
            return formatDate(value);
        },
    },
    {
        field: "actions",
        headerName: "Actions",
        display: "flex",
        type: "actions",
        getActions: ({ row }: GridRowParams<ICarrier>) => [
            <EditCarrierButton carrier={row} />,
            <DeleteCarrierButton carrierId={row.id} />,
        ],
    },
];

export const CarriersTable = () => {
    const [rows, setRows] = useState<ICarrier[]>([]);

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: "carrier_name", sort: "asc" },
    ]);

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const autoSizeOptions = {
        columns: ["carrier_name", "created_at"],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-carriers",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getAllCarriers(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || undefined
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <div className="mb-4">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    onClear={handleSearchClear}
                    placeholder="Search carriers..."
                />
            </div>
            <Table
                loading={isFetching}
                columns={columns}
                rows={rows}
                disableColumnResize
                disableColumnFilter
                autosizeOptions={autoSizeOptions}
                autoSize
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                rowCount={rowCount}
                onPaginationModelChange={setPaginationModel}
                paginationMode={"server"}
                sortingMode={"server"}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
            />
        </>
    );
};
